<template>
  <!-- 
    CardBackground Component
    ========================
    Main Orchestrator: Manages the entire card background system
    
    This component:
    - Provides card data to the conveyor system
    - Manages viewport calculations
    - Handles data fetching (mock for now, AJAX later)
    - Coordinates between all sub-components
    - Provides configuration to child components
  -->
  <div 
    class="card-background-container"
    ref="containerElement"
  >
    <!-- 
      The main conveyor system
      Handles all scrolling and row management
    -->
    <CardConveyor
      :card-data="cardDataPool"
      :cards-per-row="cardsPerRow"
      :scroll-speed="scrollSpeed"
      :row-height="rowHeight"
      :card-stagger-delay="30"
      :paused="isPaused"
      @ready="handleConveyorReady"
      @viewport-changed="handleViewportChanged"
    />
    
    <!-- Debug info (remove in production) -->
    <div v-if="showDebugInfo" class="debug-info">
      <div>Viewport: {{ viewport.width }}x{{ viewport.height }}</div>
      <div>Cards per row: {{ cardsPerRow }}</div>
      <div>Total cards: {{ cardDataPool.length }}</div>
      <div>Status: {{ status }}</div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import CardConveyor from './CardConveyor.vue'

// Import the centralized configuration and helper functions
// This ensures all animation parameters are managed in one place
import { ANIMATION_CONFIG, getScrollSpeed, getRowHeight, getCardsPerRow } from './animation-config.js'

export default {
  name: 'CardBackground',
  
  components: {
    CardConveyor
  },
  
  setup() {
    /**
     * Component State
     * ===============
     */
    
    // Element refs
    const containerElement = ref(null)
    
    // Viewport tracking
    const viewport = reactive({
      width: 0,
      height: 0,
      pixelRatio: 1
    })
    
    // Component status
    const status = ref('initializing')
    const isPaused = ref(false)
    const showDebugInfo = ref(false) // Set to true for debugging
    const preloadedImages = ref(new Map()) // Cache for preloaded images
    
    /**
     * Card Data Management
     * ====================
     * For now, we use mock data with alternating Kangaskhan and Charizard cards
     * Later, this will be replaced with AJAX calls to fetch real card data
     */
    
    // Expanded card collection for more variety
    const cardPaths = [
      '/images/kangaskhan.jpg',
      '/images/pokemon-card-1.gif',
      '/images/pokemon-card-2.gif',
      '/images/pokemon-card-3.gif',
      '/images/pokemon-card-4.gif',
      '/images/pokemon-card-5.gif',
      '/images/kangaskhan.jpg',
      '/images/pokemon-card-1.gif',
      '/images/pokemon-card-2.gif',
      '/images/pokemon-card-3.gif',
      '/images/pokemon-card-4.gif',
      '/images/pokemon-card-5.gif'
    ]
    
    /**
     * Generate a pool of card data with more variety
     * This creates enough variety for the infinite scroll
     * Each card gets a unique ID for tracking
     */
    const generateCardPool = () => {
      const pool = []
      const poolSize = ANIMATION_CONFIG.performance.cardDataPoolSize
      
      // Shuffle the card paths to create random distribution
      const shuffleArray = (arr) => {
        const newArr = [...arr]
        for (let i = newArr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[newArr[i], newArr[j]] = [newArr[j], newArr[i]]
        }
        return newArr
      }
      
      // Create multiple shuffled sets
      for (let i = 0; i < poolSize; i++) {
        const shuffled = i % cardPaths.length === 0 ? shuffleArray(cardPaths) : cardPaths
        const pathIndex = i % shuffled.length
        const cardName = shuffled[pathIndex].includes('kangaskhan') ? 'Kangaskhan' : `Pokemon Card ${pathIndex}`
        
        pool.push({
          id: `card-${i}-${Date.now()}`,
          src: shuffled[pathIndex],
          alt: `${cardName} Trading Card`,
          type: `card-type-${pathIndex}`
        })
      }
      
      return pool
    }
    
    // Initialize card data pool
    const cardDataPool = ref(generateCardPool())
    
    /**
     * Responsive Configuration
     * ========================
     * Calculate optimal settings based on viewport
     */
    
    // Calculate cards per row based on viewport width
    const cardsPerRow = computed(() => {
      const width = viewport.width || window.innerWidth
      return getCardsPerRow(width)
    })
    
    // Calculate row height based on viewport
    const rowHeight = computed(() => {
      const width = viewport.width || window.innerWidth
      return getRowHeight(width)
    })
    
    // Scroll speed based on device and viewport
    const scrollSpeed = computed(() => {
      const width = viewport.width || window.innerWidth
      return getScrollSpeed(width)
    })
    
    /**
     * Viewport Management
     * ===================
     */
    
    const updateViewport = () => {
      if (!containerElement.value) {
        // Use window dimensions on initial load
        viewport.width = window.innerWidth
        viewport.height = window.innerHeight * 0.8
        viewport.pixelRatio = window.devicePixelRatio || 1
        return
      }
      
      const rect = containerElement.value.getBoundingClientRect()
      viewport.width = rect.width
      viewport.height = rect.height
      viewport.pixelRatio = window.devicePixelRatio || 1
    }
    
    /**
     * Event Handlers
     * ==============
     */
    
    const handleConveyorReady = () => {
      status.value = 'running'
      console.log('Card conveyor system ready')
    }
    
    const handleViewportChanged = (newViewport) => {
      console.log('Viewport changed:', newViewport)
      // Could trigger data refresh or other actions
    }
    
    /**
     * Data Fetching (Future)
     * ======================
     * These methods will handle AJAX calls to fetch card data
     */
    
    const fetchCardData = async () => {
      // TODO: Replace with actual API call
      // const response = await fetch('/api/cards/random')
      // const data = await response.json()
      // return data.cards
      
      // For now, return mock data
      return generateCardPool()
    }
    
    const refreshCardData = async () => {
      status.value = 'refreshing'
      try {
        const newCards = await fetchCardData()
        cardDataPool.value = newCards
        status.value = 'running'
      } catch (error) {
        console.error('Failed to refresh card data:', error)
        status.value = 'error'
      }
    }
    
    /**
     * Performance Monitoring
     * ======================
     */
    
    const monitorPerformance = () => {
      // Check if animation is running smoothly
      let lastTime = performance.now()
      let frameCount = 0
      
      const checkFPS = () => {
        frameCount++
        const currentTime = performance.now()
        
        if (currentTime - lastTime >= 1000) {
          const fps = frameCount
          frameCount = 0
          lastTime = currentTime
          
          // Log if FPS drops below 30
          if (fps < 30) {
            console.warn(`Low FPS detected: ${fps}`)
          }
        }
        
        if (status.value === 'running') {
          requestAnimationFrame(checkFPS)
        }
      }
      
      // Start monitoring
      if (process.env.NODE_ENV === 'development') {
        // checkFPS() // Uncomment to enable FPS monitoring
      }
    }
    
    /**
     * Image Preloading Strategy
     * =========================
     * Preload images intelligently to reduce loading delays
     */
    
    const preloadImages = async (imagePaths) => {
      const preloadPromises = imagePaths.map(path => {
        // Check if already preloaded
        if (preloadedImages.value.has(path)) {
          return Promise.resolve()
        }
        
        return new Promise((resolve, reject) => {
          const img = new Image()
          
          img.onload = () => {
            // Store in cache with timestamp
            preloadedImages.value.set(path, {
              element: img,
              loadedAt: Date.now(),
              size: img.naturalWidth * img.naturalHeight
            })
            resolve()
          }
          
          img.onerror = () => {
            console.warn(`Failed to preload image: ${path}`)
            resolve() // Don't block on failures
          }
          
          img.src = path
        })
      })
      
      await Promise.allSettled(preloadPromises)
    }
    
    /**
     * Clean up old preloaded images to free memory
     */
    const cleanupPreloadedImages = () => {
      const maxAge = 60000 // 1 minute
      const maxCacheSize = ANIMATION_CONFIG.timing.preloadCacheSize
      const now = Date.now()
      
      // Remove old images
      for (const [path, data] of preloadedImages.value.entries()) {
        if (now - data.loadedAt > maxAge) {
          preloadedImages.value.delete(path)
        }
      }
      
      // If still too many, remove oldest
      if (preloadedImages.value.size > maxCacheSize) {
        const sorted = Array.from(preloadedImages.value.entries())
          .sort((a, b) => a[1].loadedAt - b[1].loadedAt)
        
        const toRemove = sorted.slice(0, sorted.length - maxCacheSize)
        toRemove.forEach(([path]) => preloadedImages.value.delete(path))
      }
    }
    
    /**
     * Lifecycle Hooks
     * ===============
     */
    
    let resizeObserver = null
    let cleanupInterval = null
    
    onMounted(async () => {
      // Preload initial card images
      await preloadImages(cardPaths.slice(0, 6)) // Preload first 6 images
      
      // Calculate viewport immediately for initial render
      updateViewport()
      
      await nextTick()
      
      // Recalculate with actual element dimensions
      updateViewport()
      
      // Setup resize observer for responsive behavior
      if (containerElement.value) {
        resizeObserver = new ResizeObserver(() => {
          updateViewport()
        })
        resizeObserver.observe(containerElement.value)
      }
      
      // Add window resize listener as backup
      const handleWindowResize = () => {
        updateViewport()
      }
      window.addEventListener('resize', handleWindowResize)
      
      // Store listener for cleanup
      window._cardBgResizeHandler = handleWindowResize
      
      // Start performance monitoring in dev
      monitorPerformance()
      
      // Start image cache cleanup interval
      cleanupInterval = setInterval(cleanupPreloadedImages, 30000) // Every 30 seconds
      
      // Optional: Refresh card data periodically
      // setInterval(refreshCardData, 60000) // Every minute
    })
    
    onUnmounted(() => {
      // Cleanup
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      
      // Remove window resize listener
      if (window._cardBgResizeHandler) {
        window.removeEventListener('resize', window._cardBgResizeHandler)
        delete window._cardBgResizeHandler
      }
      
      // Clear cleanup interval
      if (cleanupInterval) {
        clearInterval(cleanupInterval)
      }
      
      // Clear preloaded images
      preloadedImages.value.clear()
      
      status.value = 'stopped'
    })
    
    /**
     * Public API
     * ==========
     * Methods that can be called from parent components
     */
    
    const pause = () => {
      isPaused.value = true
      status.value = 'paused'
    }
    
    const resume = () => {
      isPaused.value = false
      status.value = 'running'
    }
    
    const toggleDebug = () => {
      showDebugInfo.value = !showDebugInfo.value
    }
    
    return {
      // Template refs
      containerElement,
      
      // State
      viewport,
      status,
      isPaused,
      showDebugInfo,
      cardDataPool,
      
      // Computed
      cardsPerRow,
      rowHeight,
      scrollSpeed,
      
      // Methods
      handleConveyorReady,
      handleViewportChanged,
      pause,
      resume,
      toggleDebug,
      refreshCardData
    }
  }
}
</script>

<style scoped>
/**
 * Card Background Container Styles
 * =================================
 * Main container for the entire card background system
 */

.card-background-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
  
  /* Ensure container fills parent */
  width: 100%;
  height: 100%;
  
  /* Make background subtle but visible */
  opacity: 0.25; /* Subtle but visible background effect */
  
  /* Add gradient mask that fades cards to transparent at top (for bottom-up animation) */
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 10%,        /* Fully transparent at top */
    rgba(0, 0, 0, 0.2) 30%,      /* Almost gone */
    rgba(0, 0, 0, 0.4) 50%,      /* More fade */
    rgba(0, 0, 0, 0.6) 70%,      /* Continue fading */
    rgba(0, 0, 0, 0.8) 85%,      /* Slightly fade */
    rgba(0, 0, 0, 0.9) 100%      /* More visible at bottom */
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 10%,
    rgba(0, 0, 0, 0.2) 30%,
    rgba(0, 0, 0, 0.4) 50%,
    rgba(0, 0, 0, 0.6) 70%,
    rgba(0, 0, 0, 0.8) 85%,
    rgba(0, 0, 0, 0.9) 100%
  );
}

/**
 * Debug Information Panel
 * =======================
 * Shows useful debugging info in development
 */
.debug-info {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 10px;
  border-radius: 4px;
  z-index: 9999;
  pointer-events: auto;
}

.debug-info div {
  margin-bottom: 5px;
}

.debug-info div:last-child {
  margin-bottom: 0;
}

/* Performance optimizations */
.card-background-container * {
  /* GPU acceleration */
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
}

/* Ensure smooth animations on mobile */
@media (max-width: 768px) {
  .card-background-container {
    /* Reduce complexity on mobile */
    transform: scale(1);
  }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .card-background-container {
    /* Optimize for retina displays */
    image-rendering: -webkit-optimize-contrast;
  }
}

/* Accessibility - respect reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .card-background-container {
    /* Could disable or slow animations */
    /* For now, keep animations as they're part of the design */
  }
}
</style>